/**
 * CoachPlaybook - Play Utilities
 * Version: 0.6.0
 * Description: CRUD operations and utilities for plays management (multi-sport aware)
 */

import { openDB, STORES } from '../db';

// Play Types
export const PLAY_TYPES = {
  OFFENSE: 'offense',
  DEFENSE: 'defense',
  TRANSITION: 'transition',
  OUT_OF_BOUNDS: 'outofbounds'
};

export const PLAY_CATEGORIES = {
  offense: [
    { id: 'set-play', label: 'Set Play' },
    { id: 'motion-offense', label: 'Motion Offense' },
    { id: 'isolation', label: 'Isolation' },
    { id: 'pick-and-roll', label: 'Pick and Roll' },
    { id: 'post-up', label: 'Post Up' },
    { id: 'fast-break', label: 'Fast Break' }
  ],
  defense: [
    { id: 'man-to-man', label: 'Man to Man' },
    { id: 'zone', label: 'Zone Defense' },
    { id: 'press', label: 'Press' },
    { id: 'trap', label: 'Trap' }
  ],
  transition: [
    { id: 'press-break', label: 'Press Break' },
    { id: 'secondary-break', label: 'Secondary Break' },
    { id: 'early-offense', label: 'Early Offense' }
  ],
  outofbounds: [
    { id: 'blob', label: 'BLOB (Baseline)' },
    { id: 'slob', label: 'SLOB (Sideline)' },
    { id: 'full-court', label: 'Full Court Inbound' },
    { id: 'ato', label: 'After Timeout' }
  ]
};

export const PLAY_SITUATIONS = [
  { id: 'oob-baseline', label: 'Out of Bounds - Baseline', icon: '📍' },
  { id: 'oob-sideline', label: 'Out of Bounds - Sideline', icon: '📍' },
  { id: 'after-timeout', label: 'After Timeout', icon: '⏸️' },
  { id: 'buzzer-beater', label: 'Buzzer Beater', icon: '🚨' },
  { id: 'end-of-quarter', label: 'End of Quarter', icon: '⏱️' },
  { id: 'late-game', label: 'Late Game', icon: '🏆' }
];

export const PLAY_TAGS = [
  { id: 'against-zone', label: 'vs Zone', icon: '🛡️' },
  { id: 'against-man', label: 'vs Man', icon: '👤' },
  { id: 'high-percentage', label: 'High %', icon: '🎯' },
  { id: 'three-point', label: '3-Point', icon: '3️⃣' },
  { id: 'quick-hitter', label: 'Quick Hitter', icon: '⚡' },
  { id: 'multiple-actions', label: 'Multiple Actions', icon: '🔄' }
];

// Default Position colors
export const POSITION_COLORS = {
  PG: '#3b82f6',  // Blue
  SG: '#10b981',  // Green
  SF: '#f59e0b',  // Yellow/Gold
  PF: '#f97316',  // Orange
  C: '#8b5cf6'    // Purple
};

// Get position colors (supports custom overrides)
export const getPositionColors = (customColors = null) => {
  if (customColors && customColors.positions) {
    return { ...POSITION_COLORS, ...customColors.positions };
  }
  return POSITION_COLORS;
};

// ============================================================
// Multi-Sport Utility Functions
// ============================================================

// Get play types for a given sport config
export const getPlayTypesForSport = (sportConfig) => {
  if (!sportConfig?.playTypes) return PLAY_TYPES;
  // Return the sport's playTypes keys as a PLAY_TYPES-style enum
  const types = {};
  Object.keys(sportConfig.playTypes).forEach(key => {
    types[key.toUpperCase()] = key;
  });
  return types;
};

// Get position colors for a given sport config
export const getPositionColorsForSport = (sportConfig) => {
  if (!sportConfig?.positions) return POSITION_COLORS;
  const colors = {};
  sportConfig.positions.forEach(pos => {
    colors[pos.id] = pos.defaultColor;
  });
  return colors;
};

// Get play categories for a given sport config
export const getPlayCategoriesForSport = (sportConfig) => {
  if (!sportConfig?.playTypes) return PLAY_CATEGORIES;
  return sportConfig.playTypes;
};

// Get actions for a sport, optionally filtered by category
export const getActionsForSport = (sportConfig, category = null) => {
  if (!sportConfig?.actions) return [];
  if (!category) {
    // Return all actions across all categories
    return Object.values(sportConfig.actions).flat();
  }
  return sportConfig.actions[category] || [];
};

// Create a play from a sport config (multi-sport play factory)
export const createPlayFromSport = (sportConfig, options = {}) => {
  if (!sportConfig) return createEmptyPlay();

  const id = `play-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const playerCount = options.playerCount || sportConfig.playersPerSide;
  const defenderCount = options.playerCount
    ? Math.min(options.playerCount, sportConfig.maxDefenders)
    : sportConfig.maxDefenders;

  // Resolve positions from the default formation
  // Formation keys like CB1, CB2, ST1, ST2 map to base position defs (CB, ST)
  const formationPositions = sportConfig.defaultFormation?.positions || {};
  const formationKeys = Object.keys(formationPositions).slice(0, playerCount);

  // Look up base position definition by stripping trailing digits (CB1 -> CB, ST2 -> ST)
  const findPositionDef = (formationKey) => {
    const baseKey = formationKey.replace(/\d+$/, '');
    return sportConfig.positions.find(p => p.id === baseKey || p.short === baseKey) || sportConfig.positions[0];
  };

  // Build position entries from formation keys (not from positions array)
  const positions = formationKeys.map((formationKey, idx) => {
    const posDef = findPositionDef(formationKey);
    const coords = formationPositions[formationKey] || { x: 50, y: 50 };
    const hasBall = coords.hasBall === true || (idx === 0 && !formationKeys.some(k => formationPositions[k]?.hasBall));

    return {
      id: `${id}-pos-${idx + 1}`,
      positionLabel: formationKey.replace(/\d+$/, '') !== formationKey ? formationKey : (posDef.short || posDef.id),
      positionNumber: idx + 1,
      color: posDef.defaultColor || '#3b82f6',
      playerId: null,
      playerName: null,
      jerseyNumber: null,
      avatar: null,
      startX: coords.x,
      startY: coords.y,
      hasBall,
      size: 0.6,
      paths: [],
      isShooter: false,
      primaryAction: {
        description: '',
        importance: 'key',
        completionTrigger: null,
        showCheckmark: true
      },
      secondaryActions: []
    };
  });

  // Build defenders
  const defenders = positions.slice(0, defenderCount).map((pos, idx) => ({
    id: `${id}-def-${idx + 1}`,
    x: pos.startX,
    y: Math.max(pos.startY - 8, 2),
    size: 0.6,
    label: `D${idx + 1}`,
    guardingId: pos.id,
    labelColor: null
  }));

  // Determine default type/category from sport's playTypes
  const playTypeKeys = sportConfig.playTypes ? Object.keys(sportConfig.playTypes) : ['offense'];
  const defaultType = playTypeKeys[0] || 'offense';
  const defaultCategory = sportConfig.playTypes?.[defaultType]?.[0]?.id || 'set-play';

  // Determine court view
  const courtView = options.courtView || sportConfig.defaultView || 'half';

  // Determine shot clock
  let shotClockCountdown = 24000;
  if (options.ageGroup && sportConfig.ageGroups) {
    const ageConfig = sportConfig.ageGroups.find(ag => ag.id === options.ageGroup);
    if (ageConfig?.shotClock) {
      shotClockCountdown = ageConfig.shotClock * 1000;
    }
  }

  return {
    id,
    name: '',
    type: defaultType,
    category: defaultCategory,
    situations: [],
    tags: [],
    notes: '',
    sportId: sportConfig.id,
    ageGroup: options.ageGroup || null,
    steps: [],
    courtView,
    assignmentMode: 'template',
    centerLogo: null,
    teamLogo: null,
    shotClock: {
      enabled: false,
      mode: 'duration',
      countdownFrom: shotClockCountdown,
      position: 'top-right',
      size: 'medium'
    },
    displaySettings: {
      pathColorMode: 'by-position',
      iconStyle: 'circles',
      colorScheme: 'default',
      showHelpBubbles: true
    },
    positions,
    paths: [],
    passes: [],
    screens: [],
    defenders,
    defenderPaths: [],
    gifUrl: null,
    zones: [],
    timeline: {
      duration: 5,
      keyframes: [],
      phases: []
    },
    createdDate: new Date().toISOString(),
    lastModified: new Date().toISOString()
  };
};

// Create empty play template - Players pre-placed in spread formation
export const createEmptyPlay = (courtView = 'half', customColors = null) => {
  const id = `play-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const colors = getPositionColors(customColors);
  
  // Default spread positions for half court offense - well spaced
  // PG has ball by default
  const defaultPositions = {
    PG: { x: 50, y: 80, hasBall: true },   // Top of key, ball handler
    SG: { x: 85, y: 50, hasBall: false },  // Right wing (further out)
    SF: { x: 15, y: 50, hasBall: false },  // Left wing (further out)
    PF: { x: 75, y: 28, hasBall: false },  // Right elbow
    C:  { x: 50, y: 22, hasBall: false }   // High post
  };
  
  return {
    id,
    name: '',
    type: 'offense',
    category: 'set-play',
    situations: [],
    tags: [],
    notes: '',
    
    // Play steps - numbered instructions for learners
    steps: [],
    
    courtView,
    assignmentMode: 'template',
    
    // Customization
    centerLogo: null,
    teamLogo: null,
    shotClock: {
      enabled: false,
      mode: 'duration',
      countdownFrom: 24000,
      position: 'top-right',
      size: 'medium'
    },
    
    displaySettings: {
      pathColorMode: 'by-position',
      iconStyle: 'circles',
      colorScheme: 'default',
      showHelpBubbles: true
    },
    
    // Positions - PRE-PLACED in spread formation
    positions: [
      {
        id: `${id}-pos-1`,
        positionLabel: 'PG',
        positionNumber: 1,
        color: colors.PG,
        playerId: null,
        playerName: null,
        jerseyNumber: null,
        avatar: null,
        startX: defaultPositions.PG.x,
        startY: defaultPositions.PG.y,
        hasBall: defaultPositions.PG.hasBall,
        size: 0.6,  // v0.4.1: Default to smallest size
        paths: [],
        isShooter: false,
        primaryAction: {
          description: '',
          importance: 'key',
          completionTrigger: null,
          showCheckmark: true
        },
        secondaryActions: []
      },
      {
        id: `${id}-pos-2`,
        positionLabel: 'SG',
        positionNumber: 2,
        color: colors.SG,
        playerId: null,
        playerName: null,
        jerseyNumber: null,
        avatar: null,
        startX: defaultPositions.SG.x,
        startY: defaultPositions.SG.y,
        hasBall: defaultPositions.SG.hasBall,
        size: 0.6,  // v0.4.1: Default to smallest size
        paths: [],
        isShooter: false,
        primaryAction: {
          description: '',
          importance: 'key',
          completionTrigger: null,
          showCheckmark: true
        },
        secondaryActions: []
      },
      {
        id: `${id}-pos-3`,
        positionLabel: 'SF',
        positionNumber: 3,
        color: colors.SF,
        playerId: null,
        playerName: null,
        jerseyNumber: null,
        avatar: null,
        startX: defaultPositions.SF.x,
        startY: defaultPositions.SF.y,
        hasBall: defaultPositions.SF.hasBall,
        size: 0.6,  // v0.4.1: Default to smallest size
        paths: [],
        isShooter: false,
        primaryAction: {
          description: '',
          importance: 'key',
          completionTrigger: null,
          showCheckmark: true
        },
        secondaryActions: []
      },
      {
        id: `${id}-pos-4`,
        positionLabel: 'PF',
        positionNumber: 4,
        color: colors.PF,
        playerId: null,
        playerName: null,
        jerseyNumber: null,
        avatar: null,
        startX: defaultPositions.PF.x,
        startY: defaultPositions.PF.y,
        hasBall: defaultPositions.PF.hasBall,
        size: 0.6,  // v0.4.1: Default to smallest size
        paths: [],
        isShooter: false,
        primaryAction: {
          description: '',
          importance: 'key',
          completionTrigger: null,
          showCheckmark: true
        },
        secondaryActions: []
      },
      {
        id: `${id}-pos-5`,
        positionLabel: 'C',
        positionNumber: 5,
        color: colors.C,
        playerId: null,
        playerName: null,
        jerseyNumber: null,
        avatar: null,
        startX: defaultPositions.C.x,
        startY: defaultPositions.C.y,
        hasBall: defaultPositions.C.hasBall,
        size: 0.6,  // v0.4.1: Default to smallest size
        paths: [],
        isShooter: false,
        primaryAction: {
          description: '',
          importance: 'key',
          completionTrigger: null,
          showCheckmark: true
        },
        secondaryActions: []
      }
    ],
    
    // Movement paths (separate from positions for easier management)
    paths: [],
    
    // Pass lines
    passes: [],
    
    // Screens
    screens: [],
    
    // v0.4.2: Default defenders with guarding assignments
    // Positioned slightly toward basket from each offensive player
    // v0.5.45: Use guardingId (not guardingPlayerId) for consistency
    defenders: [
      { id: `${id}-def-1`, x: 50, y: 72, size: 0.6, label: 'D1', guardingId: `${id}-pos-1`, labelColor: null },
      { id: `${id}-def-2`, x: 80, y: 45, size: 0.6, label: 'D2', guardingId: `${id}-pos-2`, labelColor: null },
      { id: `${id}-def-3`, x: 20, y: 45, size: 0.6, label: 'D3', guardingId: `${id}-pos-3`, labelColor: null },
      { id: `${id}-def-4`, x: 70, y: 25, size: 0.6, label: 'D4', guardingId: `${id}-pos-4`, labelColor: null },
      { id: `${id}-def-5`, x: 50, y: 18, size: 0.6, label: 'D5', guardingId: `${id}-pos-5`, labelColor: null }
    ],
    
    // v0.4.6: Defender movement paths
    // Defenders follow their guarded player by default, but can have explicit paths
    defenderPaths: [],
    
    // Zone overlays
    zones: [],

    // Attached GIF (data URL)
    gifUrl: null,

    // Timeline
    timeline: {
      duration: 5,
      keyframes: [],
      phases: []
    },

    createdDate: new Date().toISOString(),
    lastModified: new Date().toISOString()
  };
};

// Save play to IndexedDB
export const savePlay = async (play) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.PLAYS, 'readwrite');
    const store = tx.objectStore(STORES.PLAYS);
    
    play.lastModified = new Date().toISOString();
    
    await new Promise((resolve, reject) => {
      const request = store.put(play);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    return { success: true, play };
  } catch (error) {
    console.error('Error saving play:', error);
    return { success: false, error };
  }
};

// Get all plays from IndexedDB
export const getAllPlays = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.PLAYS, 'readonly');
    const store = tx.objectStore(STORES.PLAYS);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting plays:', error);
    return [];
  }
};

// Get single play by ID
export const getPlayById = async (id) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.PLAYS, 'readonly');
    const store = tx.objectStore(STORES.PLAYS);
    
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting play:', error);
    return null;
  }
};

// Delete play
export const deletePlay = async (id) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.PLAYS, 'readwrite');
    const store = tx.objectStore(STORES.PLAYS);
    
    await new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting play:', error);
    return { success: false, error };
  }
};

// Duplicate play
export const duplicatePlay = async (play) => {
  try {
    const newId = `play-${Date.now()}`;
    const duplicatedPlay = {
      ...play,
      id: newId,
      name: `${play.name} (Copy)`,
      positions: play.positions.map((pos, idx) => ({
        ...pos,
        id: `${newId}-pos-${idx + 1}`,
        paths: pos.paths ? [...pos.paths] : []
      })),
      paths: play.paths ? play.paths.map(p => ({
        ...p,
        id: `path-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      })) : [],
      passes: play.passes ? play.passes.map(p => ({
        ...p,
        id: `pass-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      })) : [],
      screens: play.screens ? [...play.screens] : [],
      createdDate: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };
    
    const result = await savePlay(duplicatedPlay);
    return result;
  } catch (error) {
    console.error('Error duplicating play:', error);
    return { success: false, error };
  }
};

// Export play to JSON
export const exportPlayToJSON = (play) => {
  const exportData = {
    ...play,
    exportedAt: new Date().toISOString(),
    version: '0.6.0'
  };
  
  // Remove player assignments for sharing
  exportData.positions = exportData.positions.map(pos => ({
    ...pos,
    playerId: null,
    playerName: null,
    jerseyNumber: null,
    avatar: null
  }));
  
  return JSON.stringify(exportData, null, 2);
};

// Import play from JSON
export const importPlayFromJSON = (jsonString) => {
  try {
    const play = JSON.parse(jsonString);
    
    // Handle both wrapped {play: {...}} and direct play format
    const playData = play.play || play;
    
    // Validate required fields
    if (!playData.positions || !Array.isArray(playData.positions)) {
      throw new Error('Invalid play format: missing positions');
    }
    
    // Generate new ID
    const newId = `play-${Date.now()}`;
    
    return {
      ...playData,
      id: newId,
      positions: playData.positions.map((pos, idx) => ({
        ...pos,
        id: `${newId}-pos-${idx + 1}`,
        playerId: null,
        playerName: null,
        jerseyNumber: null,
        avatar: null
      })),
      paths: playData.paths || [],
      passes: playData.passes || [],
      screens: playData.screens || [],
      createdDate: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error parsing play JSON:', error);
    throw error;
  }
};

// Search plays
export const searchPlays = (plays, query) => {
  if (!query || query.trim() === '') return plays;
  
  const searchTerm = query.toLowerCase().trim();
  
  return plays.filter(play => {
    return (
      play.name?.toLowerCase().includes(searchTerm) ||
      play.notes?.toLowerCase().includes(searchTerm) ||
      play.type?.toLowerCase().includes(searchTerm) ||
      play.category?.toLowerCase().includes(searchTerm) ||
      play.tags?.some(tag => tag.toLowerCase().includes(searchTerm))
    );
  });
};

// Filter plays
export const filterPlays = (plays, filters) => {
  let filtered = [...plays];
  
  if (filters.type && filters.type !== 'all') {
    filtered = filtered.filter(p => p.type === filters.type);
  }
  
  if (filters.category && filters.category !== 'all') {
    filtered = filtered.filter(p => p.category === filters.category);
  }
  
  if (filters.situation && filters.situation !== 'all') {
    filtered = filtered.filter(p => 
      p.situations && p.situations.includes(filters.situation)
    );
  }
  
  if (filters.tag && filters.tag !== 'all') {
    filtered = filtered.filter(p => 
      p.tags && p.tags.includes(filters.tag)
    );
  }
  
  return filtered;
};

// Sort plays
export const sortPlays = (plays, sortBy, sortDir = 'asc') => {
  const sorted = [...plays];
  
  sorted.sort((a, b) => {
    let aVal, bVal;
    
    switch (sortBy) {
      case 'name':
        aVal = a.name?.toLowerCase() || '';
        bVal = b.name?.toLowerCase() || '';
        break;
      case 'date':
        aVal = new Date(a.lastModified || a.createdDate);
        bVal = new Date(b.lastModified || b.createdDate);
        break;
      case 'type':
        aVal = a.type || '';
        bVal = b.type || '';
        break;
      default:
        return 0;
    }
    
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
  
  return sorted;
};

// Generate thumbnail preview (simplified representation)
export const generatePlayThumbnail = (play) => {
  // Returns SVG path data for a mini preview
  const positions = play.positions.filter(p => p.startX !== null && p.startY !== null);
  
  if (positions.length === 0) return null;
  
  return {
    courtView: play.courtView,
    positions: positions.map(p => ({
      x: p.startX,
      y: p.startY,
      color: p.color,
      label: p.positionLabel
    })),
    paths: play.paths?.slice(0, 5) || [] // Limit for performance
  };
};
